<template>
  <v-overlay v-model="overlay" class="d-flex align-center justify-center" max-width="600">
    <v-container class="fill-height d-flex align-center justify-center" fluid max-width="600">
      <v-card class="mx-auto" max-width="600">
        <v-card-title>Additional Verification</v-card-title>
        <v-card-text>
          <div>We need to validate a bit more information for a student level</div>
          <div>You have two options: email validation or student ID upload</div>
          <v-expansion-panels>
            <v-expansion-panel title="Email Validation">
              <v-expansion-panel-text>
                <v-row>
                  <v-col>
                    <v-form
                      ref="verificationEmail"
                      v-model="isEmailFormValid"
                      @submit.prevent="onSubmitSendEmail">
                      <v-text-field
                        ref="emailField"
                        v-model="emailForm.email_address"
                        label="Email"
                        autocomplete="email"
                        :rules="[
                          rules.required,
                          rules.emailUnique(emailForm.email_address),
                          rules.email,
                          rules.emailEDU,
                        ]"
                        :error-messages="errors.email_address"></v-text-field>
                      <v-btn
                        color="primary"
                        block
                        class="rounded-lg mb-8"
                        elevation="2"
                        :disabled="!isEmailFormValid"
                        type="submit">
                        Send Email
                      </v-btn>
                    </v-form>
                  </v-col>
                </v-row>
                <v-row v-if="emailSent">
                  <v-divider></v-divider>
                  <v-col>
                    <h3 class="text-h6 mb-1">Verification</h3>
                    <div class="text-body-2 font-weight-light">
                      Enter the code we just sent to the email you provided.
                      <span class="font-weight-black text-primary">
                        {{ emailForm.email_address }}
                      </span>
                    </div>
                    <v-otp-input
                      v-model="otp"
                      class="mt-3 ms-n2"
                      length="5"
                      placeholder="0"
                      :loading="otpLoading"
                      :error="otpError"></v-otp-input>
                    <v-divider class="mt-3 mb-6"></v-divider>
                    <div class="mb-3 text-body-2">Need another <strong>code</strong>?</div>
                    <v-btn
                      color="primary"
                      size="small"
                      text="Re-send Email"
                      variant="tonal"
                      @click="onSubmitSendEmail"></v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-text>
            </v-expansion-panel>
            <v-expansion-panel title="Student ID">
              <v-expansion-panel-text>
                <v-form>
                  <v-file-input
                    v-model="file"
                    accept="image/*"
                    label="Choose a file"></v-file-input>
                  <v-btn
                    block
                    class="rounded-lg mb-8"
                    elevation="2"
                    color="primary"
                    :disabled="!file"
                    @click="uploadFile">
                    Upload
                  </v-btn>
                  <v-progress-linear
                    v-if="uploadProgress && uploadProgress > 0"
                    v-model="uploadProgress"
                    height="10"
                    color="blue"></v-progress-linear>
                  <v-alert v-if="uploadStatus" :type="uploadStatus.type" dismissible>
                    {{ uploadStatus.message }}
                  </v-alert>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>
    </v-container>
  </v-overlay>
</template>

<script lang="ts" setup>
import {
  createProgressHandler,
  getRegistrationValidationCode,
  IUploadStatus,
  UPLOAD_NOFILE,
  UPLOAD_SUCCESS,
  uploadRegistrationValidation,
  uploadValidationAssetConfirm,
  validateRegistrationValidationCode,
} from '@/controllers';
import { rules } from '@/helpers';
import { ref, toRef, watch } from 'vue';
import type { VTextField } from 'vuetify/components';

const emit = defineEmits(['studentVerified', 'studentClosed']);
const overlay = ref<boolean>(false);
const otp = ref<string>('');
const otpLoading = ref<boolean>(false);
const otpError = ref<boolean>(false);
const emailSent = ref<boolean>(false);
const emailForm = ref<{ email_address: string }>({ email_address: '' });
const isEmailFormValid = ref<boolean>(false);
const emailField = ref<InstanceType<typeof VTextField> | null>(null);
const verificationEmail = ref<string>('');
const errors = ref({ apiError: '', email_address: '' });

const file = ref<File>();
const uploadStatus = ref<IUploadStatus | undefined>();
const uploadProgress = ref<number | null>(null);
const uploadCompleted = ref(false);

interface Props {
  showStudentModal: boolean;
}
const props = defineProps<Props>();

const showStudentModal = toRef(props, 'showStudentModal', false);
watch(
  () => showStudentModal.value,
  async (value: boolean) => {
    overlay.value = value;
  },
);

watch(overlay, (newVal, oldVal) => {
  if (!newVal && oldVal) {
    emit('studentClosed');
  }
});

watch(
  () => otp.value,
  async (value) => {
    if (value.length >= 5) {
      otpError.value = false;
      otpLoading.value = true;
      try {
        const validated = await validateRegistrationValidationCode(value);
        if (validated) {
          otpError.value = false;
          emitCompleted();
        }
      } catch {
        otpError.value = true;
      }
    }
    otpLoading.value = false;
  },
);

const emitCompleted = () => {
  setTimeout(() => {
    emit('studentVerified', true);
    emit('studentClosed');
  }, 1000);
};

const onSubmitSendEmail = async () => {
  try {
    if (await getRegistrationValidationCode(emailForm.value.email_address)) {
      emailSent.value = true;
    }
  } catch {
    emailSent.value = false;
  }
};

const uploadFile = async () => {
  if (!file.value) {
    uploadStatus.value = UPLOAD_NOFILE;
    return;
  }
  try {
    const progress = createProgressHandler(uploadProgress, uploadCompleted);

    await uploadRegistrationValidation(file.value, progress).then(async () => {
      const response = await uploadValidationAssetConfirm();
      if (response) {
        emitCompleted();
      }
    });
    uploadStatus.value = UPLOAD_SUCCESS;
  } catch (error: unknown) {
    uploadStatus.value = {
      type: 'error',
      message: ('Upload failed: ' + error) as string,
    };
  }
};
</script>
